<template>
	<div>
		<span class="b360-form-label" role="heading">Pricing Flyers</span>
		<p>
			Create custom flyers that compare multiple pricing scenarios and loan
			options for your borrowers.
		</p>
		<form
			id="lendwize-pricing-form"
			class="lendwize-pricing-form"
			@submit.prevent="submitScenario"
		>
			<section class="b360-grid-row">
				<FormMaskedInputs
					class="b360-col-12"
					label="Loan Amount"
					name="loan-amount"
					type="text"
					mask="maskDollar"
					v-model="loanAmount"
					@input="handleLTVCalculation('loanAmount')"
					@blur="inputToBeCalculated = null"
					:error="$v.loanAmount.$error"
				/>
				<div
					class="error b360-col-12"
					v-if="$v.loanAmount.$error && submitStatus === 'ERROR'"
				>
					{{ this.errorMessages.loanAmount }}
				</div>

				<FormMaskedInputs
					class="b360-col-12"
					label="Sales Price"
					name="sales-price"
					type="text"
					mask="maskDollar"
					v-model="salesPrice"
					@input="handleLTVCalculation('salesPrice')"
					@blur="inputToBeCalculated = null"
					:error="$v.salesPrice.$error"
				/>
				<div
					class="error b360-col-12"
					v-if="$v.salesPrice.$error && submitStatus === 'ERROR'"
				>
					{{ this.errorMessages.salesPrice }}
				</div>

				<FormMaskedInputs
					class="b360-col-12"
					label="LTV"
					name="ltv"
					type="text"
					mask="maskPercentage"
					v-model="loanToValue"
					@input="handleLTVCalculation('ltv')"
					@blur="inputToBeCalculated = null"
				></FormMaskedInputs>

				<FormDropdowns
					v-if="showMortgageInsuranceField()"
					class="b360-col-12"
					label="Mortgage Insurance Type"
					name="mortgage-insurance-type"
					:options="options.mortgageInsuranceTypeOptions"
					v-model="selectedMortgageInsuranceType"
					:error="$v.selectedMortgageInsuranceType.$error"
				/>
				<div
					class="error b360-col-12"
					v-if="
						showMortgageInsuranceField() &&
						$v.selectedMortgageInsuranceType.$error &&
						submitStatus === 'ERROR'
					"
				>
					{{ this.errorMessages.selectedMortgageInsuranceType }}
				</div>

				<FormDropdownMultiselect
					class="b360-col-12"
					label="Loan Type"
					name="selected-loan-type"
					:options="options.loanTypeOptions.captions"
					:max="3"
					:value="LoanTypeArray"
					@select="handleLoanType($event)"
					:error="$v.LoanTypeArray.$error"
					placeholder
				/>
				<div
					class="error b360-col-12"
					v-if="$v.LoanTypeArray.$error && submitStatus === 'ERROR'"
				>
					{{ this.errorMessages.selectedLoanType }}
				</div>

				<FormDropdowns
					class="b360-col-12"
					label="Amortization Type"
					name="amortization-type"
					:options="options.amortizationTypeOptions"
					v-model="selectedAmortizationType"
					:error="$v.selectedAmortizationType.$error"
				/>
				<div
					class="error b360-col-12"
					v-if="$v.selectedAmortizationType.$error && submitStatus === 'ERROR'"
				>
					{{ this.errorMessages.selectedAmortizationType }}
				</div>

				<FormDropdowns
					v-if="isVALoanType"
					class="b360-col-12"
					label="VA Use Type"
					name="va-use-type"
					:options="options.vaUseType"
					v-model="selectedVAUseType"
					:error="$v.selectedVAUseType.$error"
				/>
				<div
					class="error b360-col-12"
					v-if="
						isVALoanType &&
						$v.selectedVAUseType.$error &&
						submitStatus === 'ERROR'
					"
				>
					{{ this.errorMessages.selectedVAUseType }}
				</div>

				<FormDropdowns
					v-if="isVALoanType"
					class="b360-col-12"
					label="Service Type"
					name="service-type"
					:min="1"
					:max="2"
					:options="options.serviceType"
					v-model="selectedServiceType"
					:error="$v.selectedServiceType.$error"
				/>
				<div
					class="error b360-col-12"
					v-if="
						isVALoanType &&
						$v.selectedServiceType.$error &&
						submitStatus === 'ERROR'
					"
				>
					{{ this.errorMessages.selectedServiceType }}
				</div>

				<FormDropdowns
					class="b360-col-12"
					label="Purpose"
					name="loan-purpose"
					:options="options.loanPurpose"
					v-model="selectedLoanPurpose"
					@change="handleLoanPurpose($event)"
					:error="$v.selectedLoanPurpose.$error"
				/>
				<div
					class="error b360-col-12"
					v-if="$v.selectedLoanPurpose.$error && submitStatus === 'ERROR'"
				>
					{{ this.errorMessages.selectedLoanPurpose }}
				</div>

				<FormDropdowns
					v-if="showLoanRefinancePurpose"
					class="b360-col-12"
					label="Refinance Purpose"
					name="refinance-purpose"
					:options="options.refinancePurpose"
					v-model="selectedRefinancePurpose"
					:error="$v.selectedRefinancePurpose.$error"
					@change="handleRefinancePurpose($event)"
				/>
				<div
					class="error b360-col-12"
					v-if="
						showLoanRefinancePurpose &&
						$v.selectedRefinancePurpose.$error &&
						submitStatus === 'ERROR'
					"
				>
					{{ this.errorMessages.selectedLoanPurpose }}
				</div>

				<FormDropdowns
					class="b360-col-12"
					v-if="showCashOutPurpose && !isVALoanType"
					label="Cash Out Purpose"
					name="cash-out-purpose"
					:options="options.cashOutPurpose"
					v-model="selectedCashOutPurpose"
					:error="$v.selectedCashOutPurpose.$error"
				/>
				<div
					class="error b360-col-12"
					v-if="
						showCashOutPurpose &&
						!isVALoanType &&
						$v.selectedCashOutPurpose.$error &&
						submitStatus === 'ERROR'
					"
				>
					{{ this.errorMessages.selectedCashOutPurpose }}
				</div>

				<FormDropdowns
					class="b360-col-12"
					v-if="showCashOutPurpose && isVALoanType"
					label="Cash Out Purpose"
					name="cash-out-purpose"
					:options="options.vaCashOutPurpose"
					v-model="selectedCashOutPurpose"
					:error="$v.selectedCashOutPurpose.$error"
				/>
				<div
					class="error b360-col-12"
					v-if="
						showCashOutPurpose &&
						isVALoanType &&
						$v.selectedCashOutPurpose.$error &&
						submitStatus === 'ERROR'
					"
				>
					{{ this.errorMessages.selectedCashOutPurpose }}
				</div>

				<FormDropdowns
					class="b360-col-12"
					label="Occupancy"
					name="occupancy"
					:options="options.occupancy"
					v-model="selectedOccupancy"
					:error="$v.selectedOccupancy.$error"
					@change="handleOccupancy()"
				/>
				<div
					class="error b360-col-12"
					v-if="$v.selectedOccupancy.$error && submitStatus === 'ERROR'"
				>
					{{ this.errorMessages.selectedOccupancy }}
				</div>

				<FormMaskedInputs
					v-if="showFinancedPropertiesField"
					class="b360-col-12"
					label="Financed Properties"
					name="finance-properties"
					v-model="propertiesFinanced"
					mask="maskFourDigits"
					:error="$v.propertiesFinanced.$error"
				/>
				<div
					class="error b360-col-12"
					v-if="
						$v.propertiesFinanced.$error &&
						submitStatus === 'ERROR' &&
						showFinancedPropertiesField
					"
				>
					{{ this.errorMessages.propertiesFinanced }}
				</div>

				<FormDropdowns
					class="b360-col-12"
					label="Property Type"
					name="property-type"
					:options="options.propertyType"
					v-model="selectedPropertyType"
					:error="$v.selectedPropertyType.$error"
				/>
				<div
					class="error b360-col-12"
					v-if="$v.selectedPropertyType.$error && submitStatus === 'ERROR'"
				>
					{{ this.errorMessages.selectedPropertyType }}
				</div>

				<FormMaskedInputs
					class="b360-col-12"
					label="Number of Units"
					name="number-of-units"
					pattern="[0-9]{1,4}"
					v-model="numberOfUnits"
					mask="maskOneDigit"
					:error="$v.numberOfUnits.$error"
				/>
				<div
					class="error b360-col-12"
					v-if="$v.numberOfUnits.$error && submitStatus === 'ERROR'"
				>
					{{ this.errorMessages.numberOfUnits }}
				</div>

				<FormMaskedInputs
					class="b360-col-12"
					label="Property ZIP"
					name="property-zip"
					pattern="[0-9]{5}"
					v-model="propertyZip"
					mask="maskZipCode"
					@input="handlePropertyZip()"
					@change-event="handlePropertyZip()"
					:error="$v.propertyZip.$error"
				/>
				<div
					class="error b360-col-12"
					v-if="$v.propertyZip.$error && submitStatus === 'ERROR'"
				>
					{{ this.errorMessages.propertyZip }}
				</div>

				<FormInputs
					v-if="showLocation"
					class="b360-col-12"
					label="County"
					name="county"
					type="text"
					:disabled="true"
					:value="propertyCounty"
				></FormInputs>

				<FormInputs
					v-if="showLocation"
					class="b360-col-12"
					label="State"
					name="state"
					type="text"
					:disabled="true"
					v-model="propertyState"
				></FormInputs>

				<FormMaskedInputs
					class="b360-col-12"
					label="Credit Score"
					name="credit-score"
					pattern="[0-9]{3}"
					v-model="creditScore"
					mask="maskCreditScore"
					:error="$v.creditScore.$error"
				/>
				<div
					class="error b360-col-12"
					v-if="$v.creditScore.$error && submitStatus === 'ERROR'"
				>
					{{ this.errorMessages.creditScore }}
				</div>

				<FormDropdowns
					class="b360-col-12"
					label="Commitment Period"
					name="commitment-period"
					:options="options.commitmentPeriod"
					v-model="selectedCommitmentPeriod"
					:error="$v.selectedCommitmentPeriod.$error"
				/>
				<div
					class="error b360-col-12"
					v-if="$v.selectedCommitmentPeriod.$error && submitStatus === 'ERROR'"
				>
					{{ this.errorMessages.selectedCommitmentPeriod }}
				</div>

				<FormDropdowns
					class="b360-col-12"
					label="Compensation Type"
					name="compensation-type"
					:options="options.compensationType"
					v-model="selectedCompensationType"
					:error="$v.selectedCompensationType.$error"
				/>
				<div
					class="error b360-col-12"
					v-if="$v.selectedCompensationType.$error && submitStatus === 'ERROR'"
				>
					{{ this.errorMessages.selectedCompensationType }}
				</div>

				<FormDropdowns
					v-if="isGovernmentLoan"
					class="b360-col-12"
					label="Documentation Type"
					name="documentation-type"
					:options="options.documentationType"
					v-model="selectedDocumentationType"
					:error="$v.selectedDocumentationType.$error"
				/>
				<div
					class="error b360-col-12"
					v-if="
						$v.selectedDocumentationType.$error &&
						submitStatus === 'ERROR' &&
						isGovernmentLoan
					"
				>
					{{ this.errorMessages.selectedDocumentationType }}
				</div>

				<FormDropdowns
					class="b360-col-12"
					label="Escrow / Impound Waiver"
					name="escrow-impound-type"
					:options="options.escrowImpoundType"
					v-model="selectedEscrowImpoundType"
					:error="$v.selectedEscrowImpoundType.$error"
				/>
				<div
					class="error b360-col-12"
					v-if="$v.selectedEscrowImpoundType.$error && submitStatus === 'ERROR'"
				>
					{{ this.errorMessages.selectedEscrowImpoundType }}
				</div>

				<FormDropdownMultiselect
					class="b360-col-12"
					label="Loan Term"
					name="loan-term"
					:options="options.loanTerm.captions"
					v-model="loanTermArray"
					placeholder
					:min="1"
					@select="handleLoanTerm($event)"
					:error="$v.loanTermArray.$error"
				/>
				<div
					class="error b360-col-12"
					v-if="$v.loanTermArray.$error && submitStatus === 'ERROR'"
				>
					{{ this.errorMessages.selectedLoanTerm }}
				</div>

				<FormDropdowns
					class="b360-col-12"
					label="First Time Homebuyer?"
					name="first-time-homebuyer"
					:options="options.firstTimeHomebuyer"
					v-model="selectedFirstTimeHomebuyer"
					:error="$v.selectedFirstTimeHomebuyer.$error"
				/>
				<div
					class="error b360-col-12"
					v-if="
						$v.selectedFirstTimeHomebuyer.$error && submitStatus === 'ERROR'
					"
				>
					{{ this.errorMessages.selectedFirstTimeHomebuyer }}
				</div>

				<template v-if="showAdvancedOptions()">
					<FormMaskedInputs
						class="b360-col-12"
						label="Monthly Income"
						name="monthly-income"
						v-model="monthlyIncome"
						mask="maskDollar"
						:error="$v.monthlyIncome.$error"
					/>
					<div
						class="error b360-col-12"
						v-if="
							showAdvancedOptions() &&
							$v.monthlyIncome.$error &&
							submitStatus === 'ERROR'
						"
					>
						{{ this.errorMessages.monthlyIncome }}
					</div>

					<FormMaskedInputs
						class="b360-col-12"
						label="Monthly Debt"
						name="monthly-debt"
						v-model="monthlyDebt"
						mask="maskDollar"
						:error="$v.monthlyDebt.$error"
					/>
					<div
						class="error b360-col-12"
						v-if="
							showAdvancedOptions() &&
							$v.monthlyDebt.$error &&
							submitStatus === 'ERROR'
						"
					>
						{{ this.errorMessages.monthlyDebt }}
					</div>

					<FormMaskedInputs
						class="b360-col-12"
						label="Annual Taxes"
						name="annual-taxes"
						v-model="annualTaxes"
						mask="maskDollar"
						:error="$v.annualTaxes.$error"
					/>
					<div
						class="error b360-col-12"
						v-if="
							showAdvancedOptions() &&
							$v.annualTaxes.$error &&
							submitStatus === 'ERROR'
						"
					>
						{{ this.errorMessages.annualTaxes }}
					</div>

					<FormMaskedInputs
						class="b360-col-12"
						label="Annual HOI"
						name="annual-hoi"
						v-model="annualHoi"
						mask="maskDollar"
						:error="$v.annualHoi.$error"
					/>
					<div
						class="error b360-col-12"
						v-if="
							showAdvancedOptions() &&
							$v.annualHoi.$error &&
							submitStatus === 'ERROR'
						"
					>
						{{ this.errorMessages.annualHoi }}
					</div>
				</template>
			</section>

			<section class="b360-grid-row">
				<div class="b360-col-12">
					<button
						class="b360-btn b360-btn--workspace-secondary"
						@click="toggleAdvancedOptions"
						type="button"
					>
						{{ !advancedOptions ? 'Advanced Options' : 'Fewer Options' }}
					</button>
				</div>
			</section>

			<section class="b360-grid-row btn-fixed-bottom">
				<div class="b360-col-12 btn-fixed-container">
					<button class="b360-btn b360-btn--workspace-primary" type="submit">
						Submit Scenario
					</button>
				</div>
			</section>
		</form>
	</div>
</template>

<script>
// TODO: remove after refactoring
/* eslint-disable func-names */
/* eslint-disable no-restricted-globals */
/* eslint-disable eqeqeq */
    import { mapState, mapActions } from 'vuex';
import productPricingClient from '@/services/toolbox/product-and-pricing-service';
import FormInputs from '@/components/global/FormInputs.vue';
import FormMaskedInputs from '@/components/global/FormMaskedInputs.vue';
import FormDropdowns from '@/components/global/FormDropdowns.vue';
import FormDropdownMultiselect from '@/components/global/FormDropdownMultiselect.vue';
import { required, requiredIf } from 'vuelidate/lib/validators';
import {
	discaimerText,	
    lenderPriceDropDownOptions,
	errorMessages,
	loanOfficerOptions,
    lpLoanType,
	lpOccupancyType,
	lpLoanPurpose,
	lpRefinancePurpose,
    cashOutPurpose
} from './constants';
import { ERROR_DETAILS } from '@/constants/error-details';

export default {
	name: 'BbPricingScenarios',
	components: {
		FormInputs,
		FormMaskedInputs,
		FormDropdowns,
		FormDropdownMultiselect,
	},
	data() {
		return {
			pricingVals: {
				selectedLoanType: [],
				selectedLoanTerm: [],
				streamline: false,
			},
			submitStatus: null,

			// local states
			advancedOptions: false,
			showFinancedProperties: false,
			loanPurposeSelected: false,
			showLocation: false,
			inputToBeCalculated: null,
            options: lenderPriceDropDownOptions,
			errorMessages,
		};
	},
	validations: {
		loanAmount: {
			required,
		},
		salesPrice: {
			required,
		},
		loanToValue: {
			required,
		},
		selectedMortgageInsuranceType: {
			required: requiredIf(function () {
				return this.showMortgageInsuranceField();
			}),
		},
		LoanTypeArray: {
			required,
		},
		selectedAmortizationType: {
			required,
		},
		selectedVAUseType: {
			required: requiredIf(function () {
				return this.isVALoanType;
			}),
		},
		selectedServiceType: {
			required: requiredIf(function () {
				return this.isVALoanType;
			}),
		},
		selectedLoanPurpose: {
			required,
		},
		selectedRefinancePurpose: {
			required: requiredIf(function () {
				return this.showLoanRefinancePurpose;
			}),
		},
		selectedCashOutPurpose: {
			required: requiredIf(function () {
				return this.showCashOutPurpose;
			}),
		},
		selectedOccupancy: {
			required,
		},
		propertiesFinanced: {
			required: requiredIf(function () {
				return this.showFinancedPropertiesField;
			}),
		},
		selectedPropertyType: {
			required,
		},
		numberOfUnits: {
			required,
		},
		propertyZip: {
			required,
			isValidZipcode() {
				if (!this.propertyCounty || !this.propertyState) return false;
				return true;
			},
		},
		creditScore: {
			required,
		},
		selectedCommitmentPeriod: {
			required,
		},
		selectedCompensationType: {
			required,
		},
		selectedDocumentationType: {
			required: requiredIf(function () {
				return this.isGovernmentLoan;
			}),
		},
		selectedEscrowImpoundType: {
			required,
		},
		loanTermArray: {
			required,
		},
		selectedFirstTimeHomebuyer: {
			required,
		},
		monthlyIncome: {
			required: requiredIf(function () {
				return this.showAdvancedOptions();
			}),
		},
		monthlyDebt: {
			required: requiredIf(function () {
				return this.showAdvancedOptions();
			}),
		},
		annualTaxes: {
			required: requiredIf(function () {
				return this.showAdvancedOptions();
			}),
		},
		annualHoi: {
			required: requiredIf(function () {
				return this.showAdvancedOptions();
			}),
		},
	},

	computed: {
		...mapState({
			brokerId: (state) => state.lendwizePricingScenario.brokerId,
			brokerNMLS: (state) => state.lendwizePricingScenario.brokerNMLS,
			selectedLoanOfficer: (state) =>
				state.lendwizePricingScenario.selectedLoanOfficer,
			loanOfficerOptions: (state) =>
				state.lendwizePricingScenario.loanOfficerOptions,
			fhaDisclaimer: (state) => state.lendwizePricingScenario.fhaDisclaimer,
			showLoanRefinancePurpose: (state) => state.lendwizePricingScenario.selectedLoanPurpose ===
				lpLoanPurpose.Refinance,
			isVALoanType: (state) =>
				state.lendwizePricingScenario.selectedLoanType.includes(lpLoanType.VA),
			isGovernmentLoan: (state) =>
				state.lendwizePricingScenario.selectedLoanType.filter(
					(e) => e === lpLoanType.FHA || e === lpLoanType.USDA,
				).length > 0,
			isNonGovernmentLoan: (state) =>
				state.lendwizePricingScenario.selectedLoanType.filter(
					(e) => e === lpLoanType.Affordable || e === lpLoanType.Conventional,
				).length > 0,
			showCashOutPurpose: (state) =>
				state.lendwizePricingScenario.selectedRefinancePurpose ===
				lpRefinancePurpose.CashOut,
			showFinancedPropertiesField: (state) =>
				state.lendwizePricingScenario.selectedOccupancy ===
                lpOccupancyType.SecondHome ||
                state.lendwizePricingScenario.selectedOccupancy ===
                lpOccupancyType.Investment,
			propertyCounty: (state) => state.lendwizePricingScenario.propertyCounty,
			propertyState: (state) => state.lendwizePricingScenario.propertyState,
			LoanTypeArray: (state) => state.lendwizePricingScenario.selectedLoanType,
			loanTermArray: (state) => state.lendwizePricingScenario.selectedLoanTerm,
			ltvNum: (state) => state.lendwizePricingScenario.ltvNum,
		}),        

		// Vuex Getters and Setters that are binded to each field
		salesPrice: {
			get() {
				return this.$store.state.lendwizePricingScenario.salesPriceString;
			},
			set(value) {
				this.$store.dispatch('updatePricingFields', {
					fieldName: 'salesPriceString',
					value,
				});
			},
		},
		loanToValue: {
			get() {
				return this.$store.state.lendwizePricingScenario.ltvString;
			},
			set(value) {
				this.$store.dispatch('updatePricingFields', {
					fieldName: 'ltvString',
					value,
				});
			},
		},
		loanAmount: {
			get() {
				return this.$store.state.lendwizePricingScenario.loanAmountString;
			},
			set(value) {
				this.$store.dispatch('updatePricingFields', {
					fieldName: 'loanAmountString',
					value,
				});
			},
		},
		selectedMortgageInsuranceType: {
			get() {
				return this.$store.state.lendwizePricingScenario
					.selectedMortgageInsuranceType;
			},
			set(value) {
				this.$store.dispatch('updatePricingFields', {
					fieldName: 'selectedMortgageInsuranceType',
					value,
				});
			},
		},
		selectedVAUseType: {
			get() {
				return this.$store.state.lendwizePricingScenario.selectedVAUseType;
			},
			set(value) {
				this.$store.dispatch('updatePricingFields', {
					fieldName: 'selectedVAUseType',
					value,
				});
			},
		},
		selectedServiceType: {
			get() {
				return this.$store.state.lendwizePricingScenario.selectedServiceType;
			},
			set(value) {
				this.$store.dispatch('updatePricingFields', {
					fieldName: 'selectedServiceType',
					value,
				});
			},
		},
		selectedAmortizationType: {
			get() {
				return this.$store.state.lendwizePricingScenario
					.selectedAmortizationType;
			},
			set(value) {
				this.$store.dispatch('updatePricingFields', {
					fieldName: 'selectedAmortizationType',
					value,
				});
			},
		},
		selectedLoanPurpose: {
			get() {
				return this.$store.state.lendwizePricingScenario.selectedLoanPurpose;
			},
			set(value) {
				this.$store.dispatch('updatePricingFields', {
					fieldName: 'selectedLoanPurpose',
					value,
				});
			},
		},
		selectedRefinancePurpose: {
			get() {
				return this.$store.state.lendwizePricingScenario
					.selectedRefinancePurpose;
			},
			set(value) {
				this.$store.dispatch('updatePricingFields', {
					fieldName: 'selectedRefinancePurpose',
					value,
				});
			},
		},
		selectedCashOutPurpose: {
			get() {
				return this.$store.state.lendwizePricingScenario.selectedCashOutPurpose;
			},
			set(value) {
				this.$store.dispatch('updatePricingFields', {
					fieldName: 'selectedCashOutPurpose',
					value,
				});
			},
		},
		selectedOccupancy: {
			get() {
				return this.$store.state.lendwizePricingScenario.selectedOccupancy;
			},
			set(value) {
				this.$store.dispatch('updatePricingFields', {
					fieldName: 'selectedOccupancy',
					value,
				});
			},
		},
		propertiesFinanced: {
			get() {
				return this.$store.state.lendwizePricingScenario.propertiesFinanced;
			},
			set(value) {
				this.$store.dispatch('updatePricingFields', {
					fieldName: 'propertiesFinanced',
					value,
				});
			},
		},
		selectedPropertyType: {
			get() {
				return this.$store.state.lendwizePricingScenario.selectedPropertyType;
			},
			set(value) {
				this.$store.dispatch('updatePricingFields', {
					fieldName: 'selectedPropertyType',
					value,
				});
			},
		},
		numberOfUnits: {
			get() {
				return this.$store.state.lendwizePricingScenario.numberOfUnits;
			},
			set(value) {
				if (parseInt(value, 10) > 4) {
					this.$store.dispatch('updatePricingFields', {
						fieldName: 'numberOfUnits',
						value: 1,
					});
				} else {
					this.$store.dispatch('updatePricingFields', {
						fieldName: 'numberOfUnits',
						value,
					});
				}
			},
		},
		propertyZip: {
			get() {
				return this.$store.state.lendwizePricingScenario.propertyZip;
			},
			set(value) {
				this.$store.dispatch('updatePricingFields', {
					fieldName: 'propertyZip',
					value,
				});
			},
		},
		creditScore: {
			get() {
				return this.$store.state.lendwizePricingScenario.creditScore;
			},
			set(value) {
				this.$store.dispatch('updatePricingFields', {
					fieldName: 'creditScore',
					value,
				});
			},
		},
		selectedCommitmentPeriod: {
			get() {
				return this.$store.state.lendwizePricingScenario
					.selectedCommitmentPeriod;
			},
			set(value) {
				this.$store.dispatch('updatePricingFields', {
					fieldName: 'selectedCommitmentPeriod',
					value,
				});
			},
		},
		selectedCompensationType: {
			get() {
				return this.$store.state.lendwizePricingScenario
					.selectedCompensationType;
			},
			set(value) {
				this.$store.dispatch('updatePricingFields', {
					fieldName: 'selectedCompensationType',
					value,
				});
			},
		},
		selectedDocumentationType: {
			get() {
				return this.$store.state.lendwizePricingScenario
					.selectedDocumentationType;
			},
			set(value) {
				this.$store.dispatch('updatePricingFields', {
					fieldName: 'selectedDocumentationType',
					value,
				});
			},
		},
		selectedEscrowImpoundType: {
			get() {
				return this.$store.state.lendwizePricingScenario
					.selectedEscrowImpoundType;
			},
			set(value) {
				this.$store.dispatch('updatePricingFields', {
					fieldName: 'selectedEscrowImpoundType',
					value,
				});
			},
		},
		selectedFirstTimeHomebuyer: {
			get() {
				return this.$store.state.lendwizePricingScenario
					.selectedFirstTimeHomebuyer;
			},
			set(value) {
				this.$store.dispatch('updatePricingFields', {
					fieldName: 'selectedFirstTimeHomebuyer',
					value,
				});
			},
		},
		monthlyIncome: {
			get() {
				return this.$store.state.lendwizePricingScenario.monthlyIncome;
			},
			set(value) {
				this.$store.dispatch('updatePricingFields', {
					fieldName: 'monthlyIncome',
					value,
				});
			},
		},
		monthlyDebt: {
			get() {
				return this.$store.state.lendwizePricingScenario.monthlyDebt;
			},
			set(value) {
				this.$store.dispatch('updatePricingFields', {
					fieldName: 'monthlyDebt',
					value,
				});
			},
		},
		annualTaxes: {
			get() {
				return this.$store.state.lendwizePricingScenario.annualTaxes;
			},
			set(value) {
				this.$store.dispatch('updatePricingFields', {
					fieldName: 'annualTaxes',
					value,
				});
			},
		},
		annualHoi: {
			get() {
				return this.$store.state.lendwizePricingScenario.annualHoi;
			},
			set(value) {
				this.$store.dispatch('updatePricingFields', {
					fieldName: 'annualHoi',
					value,
				});
			},
		},
	},

	methods: {
		...mapActions([
			'updatePricingFields',
			'updatePricingSubheading',
			'updateFHADisclaimerText',
			'updateLocation',
			'updateBrokerInfo',
			'showErrorModal',
			'showApiErrorModal',
		]),
		scrollToError() {
			const topPadding = 5;
			const form = document.querySelector('#bb-nested-view');
			const nav = document.querySelector('#top-nav').getBoundingClientRect();
			this.$nextTick(() => {
				let errorDiv = document.querySelector('.error');
				errorDiv = errorDiv.previousElementSibling;
				const domRect = errorDiv.getBoundingClientRect();
				form.scrollTo({
					top: domRect.top + form.scrollTop - nav.height - topPadding,
					left: domRect.left,
					behavior: 'smooth',
				});
			});
		},
		submitScenario() {
			// vuelidate - check if from is valid
			this.$v.$touch();
			if (this.$v.$invalid) {
				this.submitStatus = 'ERROR';
				this.scrollToError();
				return;
			}

			// ensure that brokerNMLS has contents
			if (
				this.brokerNMLS !== null &&
				this.brokerNMLS.length > 0 &&
				this.brokerId !== null &&
				this.brokerId.length > 0
			) {
				const fundingFeeType = this.isGovernmentLoan ? 'EXEMPT' : 'FINANCED';
                if (
                    this.selectedLoanPurpose === lpLoanPurpose.Purchase &&
                    this.selectedRefinancePurpose !== lpLoanPurpose.Purchase
                ) {
                    this.selectedRefinancePurpose = lpLoanPurpose.Purchase;
                }

                if (
                    this.isVALoanType &&
                    this.selectedLoanPurpose === lpLoanPurpose.Refinance &&
                    this.selectedRefinancePurpose === lpRefinancePurpose.RateAndTerm
                ) {
                    this.selectedCashOutPurpose = cashOutPurpose.VAIRRRL;
                }	

				if (
					this.isGovernmentLoan &&
					!this.isNonGovernmentLoan &&
					// TODO: refactor next 2 lines to use !==
					this.selectedCashOutPurpose != cashOutPurpose.VATypeOne &&
					this.selectedCashOutPurpose != cashOutPurpose.VATypeTwo
				) {
					this.streamline = true;
				} else {
					this.streamline = false;
				}

				// package the arguments
				const args = {
					loanOfficerNMLSNumber: `${this.brokerNMLS}`,
					brokerId: this.brokerId,
					loanLimitType: 'CONFORMING', // TODO: Confirm
					fundingFeeAmount: 0,
					amortizationType: this.selectedAmortizationType,
					loanAmount: this.prepFloat(this.loanAmount),
					loanPurpose: this.selectedLoanPurpose,
					loanPurposeType: this.selectedRefinancePurpose,
					cashOutPurpose: this.selectedCashOutPurpose,
					escrow: this.selectedEscrowImpoundType,
					loanDocType: this.selectedDocumentationType,
					residencyStatus: 'US_CITIZEN/PERM_RES',
					mortgageTypes: this.convertToValues(
						this.LoanTypeArray,
						this.options.loanTypeOptions,
					),
					borrowerPaidComp: this.selectedCompensationType,
					fundingFeeType,
					loanTerm: this.convertToValues(
						this.loanTermArray,
						this.options.loanTerm,
					),
					lockPeriod: this.selectedCommitmentPeriod,
					creditScore: parseInt(this.creditScore, 10),
					vaServiceType: this.selectedServiceType,
					vaFirstTimeUse: this.selectedVAUseType,
					vaServiceDisability: false,
					monthlyDebt: this.prepFloat(this.monthlyDebt),
					firstTimeHomeBuyer: this.selectedFirstTimeHomebuyer,
					propertyValue: this.prepFloat(this.salesPrice),
					propertyUsageType: this.selectedOccupancy,
					propertiesFinanced: parseInt(this.propertiesFinanced, 10),
					propertyType: this.selectedPropertyType,
					numberOfUnits: parseInt(this.numberOfUnits, 10),
					hazardInsurance: 0,
					propertyTaxes: 0,
					homeownerAssociation: 0,
					zipcode: this.propertyZip !== null ? `${this.propertyZip}` : null,
					monthlyIncome: this.prepFloat(this.monthlyIncome),
					propertyCounty: this.propertyCounty,
					propertyState: this.propertyState,
					lenderPaidMI: this.selectedMortgageInsuranceType,
					streamline: this.streamline,
				};

				productPricingClient
					.getProductAndPricing(args)
					.then((response) => {
						if (response.data.responseType.httpStatusCode !== 200) {
							// Trigger the error page only if you do not recieve a success 200 message.
							this.navigateToError();
							return;
						}
						const eligibleProducts = [];

						// TODO: fix map, pushes to array, does not use map properly
						// eslint-disable-next-line array-callback-return
						response.data.eligibleProducts.map((e, index) => {
							const productId = index + 1;
							const monthlyTaxes = parseFloat(
								this.prepFloat(this.annualTaxes) / 12,
							).toFixed(2);
							const monthlyHoi = parseFloat(
								this.prepFloat(this.annualHoi) / 12,
							).toFixed(2);

							const productGroup = {
								categoryId: productId,
								categoryName: e.productName,
								categoryNameRef: `product-${productId}`,
								optionsDisplayLimit: 3,
								show: true,
								products: e.products.map((f) => ({
									productName: f.productName,
									interest: `${f.rate}`,
									monthlyPayment: `${parseFloat(f.principalAndInterest).toFixed(
										2,
									)}`,
									finalPrice: `${parseFloat(f.finalDiscountPoints).toFixed(
										2,
									)} / $${parseFloat(f.finalDiscountPointsDollar).toFixed(2)}`,
									selected: false,
									annualPercentageRate: `${parseFloat(
										f.annualPercentageRate,
									).toFixed(2)}`,
									mortgageInsuranceAnnualDollar: `${parseFloat(
										f.mortgageInsuranceAnnualDollar,
									).toFixed(2)}`,
									taxes: monthlyTaxes,
									insurance: monthlyHoi,
									financeCharges: f.fees
										.map((x) => x.totalAmount)
										.reduce((total, amount) => total + amount),
								})),
							};

							eligibleProducts.push(productGroup);
						});

						this.updatePricingFields({
							fieldName: 'productData',
							value: eligibleProducts,
						});

						const ineligibleProducts = [];

						// TODO: refactor to properly use map
						// eslint-disable-next-line array-callback-return
						response.data.ineligibleProducts.map((e, index) => {
							const productId = index + 1;

							ineligibleProducts.push({
								categoryId: productId,
								name: e.name,
								reasons: e.eligibility,
								show: false,
							});
						});

						this.updatePricingFields({
							fieldName: 'ineligibleData',
							value: ineligibleProducts,
						});

						this.navigateToProductsForm();
					})
					.catch(() => {
						this.navigateToError();
					});
			}
		},

		// Event handlers for form input
		// -- trigger conditionally rendered fields and side effects of user input
		handleOccupancy() {
			if (!this.showFinancedPropertiesField) {
				this.propertiesFinanced = '1';
			}
		},
		handleLoanType(data) {
			this.pricingVals.selectedLoanType = this.handleArray(
				data,
				this.pricingVals.selectedLoanType,
			);

			// update state
			this.updatePricingFields({
				fieldName: 'selectedLoanType',
				value: this.pricingVals.selectedLoanType,
			});

			this.selectedRefinancePurpose = null;
			this.selectedCashOutPurpose = null;

			if (!this.isGovernmentLoan) {
				this.selectedDocumentationType = 'Full Doc - 24M';
			}

			if (!this.isVALoanType) {
				this.selectedVAUseType = null;
				this.selectedServiceType = null;
			}

			if (
				this.fhaDisclaimer === null &&
				this.LoanTypeArray.includes(lpLoanType.FHA)
			) {
				this.updateFHADisclaimerText(discaimerText.FHA);
			} else if (
				this.fhaDisclaimer !== null &&
				!this.LoanTypeArray.includes(lpLoanType.FHA)
			) {
				this.updateFHADisclaimerText(null);
			}
		},
		handleLoanTerm(data) {
			this.pricingVals.selectedLoanTerm = this.handleArray(
				data,
				this.pricingVals.selectedLoanTerm,
			);
			this.updatePricingFields({
				fieldName: 'selectedLoanTerm',
				value: this.pricingVals.selectedLoanTerm,
			});
		},
		handleLoanPurpose(data) {
			this.loanPurposeSelected = true;
			this.updatePricingText();
			
			if (data.value === lpRefinancePurpose.Purchase) {
				this.selectedCashOutPurpose = null;
				this.selectedRefinancePurpose = lpRefinancePurpose.Purchase;
			}
		},
		handleRefinancePurpose(data) {			
			if (data.value === lpRefinancePurpose.RateAndTerm) {
				this.selectedCashOutPurpose = null;
			}
		},
		handlePropertyZip() {
			if (this.propertyZip && this.propertyZip.length >= 5) {
				productPricingClient
					.GetAddressDetails(this.propertyZip)
					.then((response) => {
						this.updateLocation({
							county: response.data.county,
							state: response.data.state,
						});
						this.showLocation = true;
					});
			} else {
				this.updateLocation({
					county: null,
					state: null,
				});
				this.showLocation = false;
			}
		},

		// Methods to conditionally render fields and views
		// -- moved most of these methods to computed properties
		showMortgageInsuranceField() {
			if (this.ltvNum > 80) {
				return true;
			}
			this.selectedMortgageInsuranceType = null;
			return false;
		},
		showAdvancedOptions() {
			return this.advancedOptions;
		},
		toggleAdvancedOptions() {
			// clear advanced field values when toggling
			this.annualTaxes = null;
			this.annualHoi = null;
			this.monthlyDebt = null;
			this.monthlyIncome = null;

			this.advancedOptions = !this.advancedOptions;
		},
		navigateToProductsForm() {
			this.updatePricingFields({
				fieldName: 'activeForm',
				value: 2,
			});
		},
		navigateToError() {
			this.updatePricingFields({
				fieldName: 'activeForm',
				value: 3,
			});
		},

		// 3 way LTV Calculation when user changes sales price, loan amount or LTV
		handleLTVCalculation(input) {
			// cleaning variables of being undefined, NaN, and of % $
			const oldSalesPrice =
				typeof this.salesPrice !== 'undefined' ? this.salesPrice : 0;
			const oldLTV =
				typeof this.loanToValue !== 'undefined' ? this.loanToValue : 0;
			const oldLoanAmount =
				typeof this.loanAmount !== 'undefined' ? this.loanAmount : 0;
			// TODO: Need to fix use of isNaN function
			const loanAmount = isNaN(oldLoanAmount)
				? parseFloat(oldLoanAmount.replace('$ ', ''))
				: oldLoanAmount;
			const salesPrice = isNaN(oldSalesPrice)
				? parseFloat(oldSalesPrice.replace('$ ', ''))
				: oldSalesPrice;
			const ltv = isNaN(oldLTV) ? parseFloat(oldLTV.replace(' %', '')) : oldLTV;

			// creating seperate ltv, salesprice and loan amount variables in state, so there is a number and string
			this.updatePricingFields({
				fieldName: 'salesPriceNum',
				value: salesPrice,
			});
			this.updatePricingFields({
				fieldName: 'ltvNum',
				value: ltv,
			});
			this.updatePricingFields({
				fieldName: 'loanAmountNum',
				value: loanAmount,
			});

			if (this.inputToBeCalculated === null) {
				this.setCalculationWedge(input, ltv, salesPrice, loanAmount);
			}
			this.calculateDyanmicInput(
				this.inputToBeCalculated,
				loanAmount,
				salesPrice,
				ltv,
			);

			// update sub header
			if (this.loanPurposeSelected) {
				this.updatePricingText();
			}
		},
		calculateLTV(loanAmount, salesPrice) {
			const ltv = parseFloat((100 / salesPrice) * loanAmount).toFixed(2);

			if (ltv && ltv >= 0 && loanAmount > 0 && salesPrice > 0) {
				this.loanToValue = `${ltv} %`;
			} else {
				this.loanToValue = '0 %';
			}
		},
		setCalculationWedge(inputField, ltv, salesPrice, loanAmount) {
			// since this field is 3 way binded - we must figure out which field will be calculated
			const thisRef = this;
			switch (inputField) {
				case 'ltv':
					if ((salesPrice && loanAmount) || salesPrice) {
						thisRef.inputToBeCalculated = 'loanAmount';
					} else if (loanAmount) {
						thisRef.inputToBeCalculated = 'salesPrice';
					}
					break;
				case 'salesPrice':
					if ((ltv && loanAmount) || loanAmount) {
						thisRef.inputToBeCalculated = 'ltv';
					} else if (ltv) {
						thisRef.inputToBeCalculated = 'loanAmount';
					}
					break;
				case 'loanAmount':
					if ((ltv && salesPrice) || salesPrice) {
						thisRef.inputToBeCalculated = 'ltv';
					} else if (ltv) {
						thisRef.inputToBeCalculated = 'salesPrice';
					}
					break;
				default:
			}
		},
		calculateDyanmicInput(inputToBeCalculated, loanAmount, salesPrice, ltv) {
			const thisRef = this;

			switch (inputToBeCalculated) {
				case 'ltv':
					thisRef.calculateLTV(loanAmount, salesPrice);
					break;
				case 'salesPrice':
					thisRef.calculateSalesPrice(loanAmount, ltv);
					break;
				case 'loanAmount':
					thisRef.calculateLoanAmount(ltv, salesPrice);
					break;
				default:
			}
		},
        calculateSalesPrice(loanAmount, ltv) {
            if (loanAmount && ltv && ltv > 0) {
                const salesPrice = parseFloat((loanAmount * 100) / ltv).toFixed(0);
                this.salesPrice = `$ ${salesPrice}`;
            }
        },
		calculateLoanAmount(ltv, salesPrice) {
			if (ltv && salesPrice) {
				const loanAmount = parseFloat((salesPrice / 100) * ltv).toFixed(0);
				this.loanAmount = `$ ${loanAmount}`;
			}
		},

		// Helper functions
		async getLoanOfficerOptions() {
			try {
				const { status, data: { brokerId, nmlsNumber: brokerNMLS } = {} } =
					(await productPricingClient.GetCompanyNMLSAndBrokerId(
						this.$store.state.profile.userId,
						0,
						0,
						this.$store.state.companyProfile.id,
					)) || {};

				if (status !== 200) {
					throw loanOfficerOptions.API_CALL_FAILED;
				}

				if (!brokerId) {
					throw loanOfficerOptions.INVALID_ID;
				} else if (!brokerNMLS) {
					throw loanOfficerOptions.INVALID_NMLS;
				}

				this.updateBrokerInfo({
					brokerId,
					brokerNMLS,
				});
			} catch (error) {
				switch (error) {
					case loanOfficerOptions.API_CALL_FAILED:
						this.showApiErrorModal({
							path: '/campaign-editor-home/',
							message:
								'There was a problem retriving information needed to create a pricing flyer.',
							buttonLabel: 'Return to Brand Builder',
						});
						break;
					case loanOfficerOptions.INVALID_ID:
					case loanOfficerOptions.INVALID_NMLS:
						this.handleLoanOfficerOptionsError();
						break;
					default:
						console.error(error);
				}
			}
		},
		handleLoanOfficerOptionsError() {
			this.showErrorModal({
				error: 'Loan officer account is not set up properly',
				errorDetails: {
					...ERROR_DETAILS.LOAN_OFFICER_OPTIONS,
					message:
						'There is an issue with your account information needed to create a pricing flyer. Please reach out to your Account Executive.',
				},
			});
		},
		updatePricingText() {
			const purpose =
				this.selectedLoanPurpose === 'REFINANCE'
					? 'Property Value'
					: 'Sales Price';
			const loanAmountText = 'Loan Amount';
			const oldSalesPrice =
				typeof this.salesPrice !== 'undefined' ? this.salesPrice : 0;
			const salesPrice = isNaN(oldSalesPrice)
				? parseFloat(oldSalesPrice.replace('$ ', ''))
				: oldSalesPrice;
			const oldLoanAmount =
				typeof this.loanAmount !== 'undefined' ? this.loanAmount : 0;
			const loanAmount = isNaN(oldLoanAmount)
				? parseFloat(oldLoanAmount.replace('$ ', ''))
				: oldLoanAmount;
			if (
				typeof salesPrice !== 'undefined' ||
				typeof loanAmount !== 'undefined'
			) {
				this.updatePricingSubheading(
					`<p>Address Line 1</p>
						<p>Address Line 2</p>
						<p>${purpose} $${salesPrice}</p>
                        <p>${loanAmountText} $${loanAmount}</p >`,
				);
			}
		},
		// TODO: refactor to not mutate arr parameter
		handleArray(data, arr) {
			if (arr !== null && arr.length > 0) {
				if (arr.filter((e) => e === data).length > 0) {
					arr.splice(arr.indexOf(data), 1);
				} else {
					arr.push(data);
				}
			} else {
				// eslint-disable-next-line no-param-reassign
				arr = [data];
			}

			return arr;
		},
		convertToValues(data, source) {
			let arr = [];

			//	TODO: refactor to eliminate eslint error
			// eslint-disable-next-line no-restricted-syntax
			for (const d of data) {
				const value = source.values
					.filter((e) => e.name === d)
					.map((e) => e.value);

				if (value.length > 0) {
					arr = arr.concat(value);
				}
			}
			return arr;
		},
		prepFloat(param) {
			if (
				param !== null &&
				param.length > 0 &&
				parseFloat(param.replace('$ ', '')) > 0
			) {
				return parseFloat(param.replace('$ ', ''));
			}
			return 0;
		},
	},

	// Lifecycle hooks
	mounted() {
		this.getLoanOfficerOptions();
		if (this.propertyCounty) {
			this.showLocation = true;
		}
		if (
			this.monthlyIncome ||
			this.monthlyDebt ||
			this.annualTaxes ||
			this.annualHOI
		) {
			this.advancedOptions = true;
		}
	},
};
</script>

<style lang="scss" scoped>
.lendwize-pricing-form {
	@include rem(margin-bottom, 62px);

	@include lg {
		@include rem(margin-bottom, 52px);
	}
}

.b360-btn--workspace-secondary {
	border: none;
}

.dirty {
	border-color: lighten(
		desaturate(adjust-hue($success, -30), 66.67),
		23.92
	); //This gives the color of #5A5;
	background: lighten(
		adjust-hue($success, -30),
		70.59
	); //This gives the color of #EFE;
}

.dirty:focus {
	outline-color: lighten(
		desaturate(adjust-hue($success, -30), 25),
		47.25
	); //This give the color of #8E8
}

.error {
	color: $error;
	margin-top: -26px;
}

.error:focus {
	outline-color: lighten(
		saturate($error, 20.8),
		30.98
	); // This gives the color of #ff9999
}

.btn-fixed-bottom {
	@include flex-center;
	@include rem(padding, 15px 0 10px);
	position: fixed;
	width: 100%;
	max-width: 460px;
	@include rem(max-width, 460px);
	left: 0;
	@include rem(bottom, 100px);

	@include lg {
		@include rem(left, 101px);
		@include rem(bottom, -16px);
	}

	.btn-fixed-container {
		width: 100%;
		text-align: center;
		box-sizing: border-box;
	}

	button {
		margin: 0 auto;
		max-width: calc(100% - 42px);
		border: none;
	}
}

@include phablet {
	@include rem(max-width, 459px);
}

@include tablet {
	@include rem(max-width, 459px);
}

@include lg {
	@include rem('max-width', 458px);
	bottom: 0;
	@include rem(left, 101px);

	button {
		@include rem(max-width, 417px);
	}
}
</style>
